import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ConfigService } from 'src/app/shared/services';

export interface OrderNumberRecord {
  id?: number;
  userID: number;

  quotationNo: string;
  quotationDate?: Date | string | null;

  piNo: string;
  piDate?: Date | string | null;

  jobNo: string;
  jobDate?: Date | string | null;

  docType: 'D' | 'E' | 'ES';        // keep ES because your DB has it
  isSample?: boolean;

  // ✅ Replacement
  isReplacement?: boolean;
  replacedFromId?: number | null;

  createdBy?: string;
  editedBy?: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}


@Injectable({
  providedIn: 'root'
})
export class OrderNumberService {
  private apiUrl: string;

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) {
    this.apiUrl = `${this.configService.getConfig()['apiURL']}order-numbers`;
  }

  createRecord(record: Partial<OrderNumberRecord>): Observable<OrderNumberRecord> {
    return this.http.post<OrderNumberRecord>(`${this.apiUrl}`, record);
  }

  getRecords(docType?: string, isSample?: boolean): Observable<OrderNumberRecord[]> {
    let url = `${this.apiUrl}`;
    const params: string[] = [];

    if (docType) {
      params.push(`docType=${docType}`);
    }
    if (isSample !== undefined) {
      params.push(`isSample=${isSample}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http.get<OrderNumberRecord[]>(url);
  }

  getRecord(id: number): Observable<OrderNumberRecord> {
    return this.http.get<OrderNumberRecord>(`${this.apiUrl}/${id}`);
  }

  updateRecord(id: number, record: Partial<OrderNumberRecord>): Observable<OrderNumberRecord> {
    return this.http.put<OrderNumberRecord>(`${this.apiUrl}/${id}`, record);
  }

  deleteRecord(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  exportToCSV(year: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/export/csv?year=${year}`, { responseType: 'blob' });
  }

  // Next number endpoints
  getNextQuotationNumber(userID: number): Observable<{ nextNumber: string }> {
    return this.http.get<{ nextNumber: string }>(`${this.apiUrl}/next-quotation/${userID}`);
  }

  getNextSampleQuotationNumber(userID: number): Observable<{ nextNumber: string }> {
    return this.http.get<{ nextNumber: string }>(`${this.apiUrl}/next-sample-quotation/${userID}`);
  }

  getNextPINumber(docType: 'D' | 'E' | 'ES'): Observable<{ nextNumber: string }> {
    return this.http.get<{ nextNumber: string }>(`${this.apiUrl}/next-pi?docType=${docType}`);
  }

  getNextJobNumber(docType: 'D' | 'E' | 'ES', isSample: boolean = false): Observable<{ nextNumber: string }> {
    return this.http.get<{ nextNumber: string }>(`${this.apiUrl}/next-job?docType=${docType}&isSample=${isSample}`);
  }

  getNextReplacementQuotationNumber(userID: number, initials: string): Observable<{ nextNumber: string }> {
    const init = encodeURIComponent((initials || '').toUpperCase());
    return this.http.get<{ nextNumber: string }>(
      `${this.apiUrl}/next-replacement/${userID}?initials=${init}`
    );
  }
}
